import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Trash2, Mail, Phone, Building, Calendar, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterService, setFilterService] = useState('all');

  // Check if already authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('kilolava_admin_token');
      if (token) {
        try {
          await axios.post(`${API}/admin/verify`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
        } catch (err) {
          // Token invalid or expired, clear it
          localStorage.removeItem('kilolava_admin_token');
        }
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success && response.data.token) {
        localStorage.setItem('kilolava_admin_token', response.data.token);
        setIsAuthenticated(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('kilolava_admin_token');
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo contatto?')) {
      try {
        await axios.delete(`${API}/contacts/${id}`);
        fetchContacts();
      } catch (err) {
        console.error('Error deleting contact:', err);
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchContacts();
    }
  }, [isAuthenticated]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filteredContacts = filterService === 'all' 
    ? contacts 
    : contacts.filter(c => c.service === filterService);

  const getServiceBadge = (service) => {
    const colors = {
      pulizie: 'bg-blue-100 text-blue-800',
      biancheria: 'bg-green-100 text-green-800',
      tovagliato: 'bg-purple-100 text-purple-800',
    };
    return colors[service] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getContactName = (contact) => {
    return contact.companyName || contact.establishmentName || contact.restaurantName || contact.name || '-';
  };

  // Login Page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 shadow-xl max-w-md w-full mx-4"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-text-primary">Admin Kilolava</h1>
            <p className="text-text-secondary mt-2">Inserisci la password per accedere</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Input
                data-testid="admin-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="h-12 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <Button
              data-testid="admin-login-button"
              type="submit"
              className="w-full bg-primary hover:bg-primary-hover text-white h-12 rounded-full"
            >
              Accedi
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-text-primary">Dashboard Admin</h1>
            <p className="text-text-secondary">Gestisci i contatti ricevuti</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="admin-refresh-button"
              variant="outline"
              onClick={fetchContacts}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            <Button
              data-testid="admin-logout-button"
              variant="outline"
              onClick={handleLogout}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Esci
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-text-muted text-sm">Totale</p>
            <p className="font-heading text-3xl font-bold text-text-primary">{contacts.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-text-muted text-sm">Pulizie</p>
            <p className="font-heading text-3xl font-bold text-blue-600">
              {contacts.filter(c => c.service === 'pulizie').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-text-muted text-sm">Biancheria</p>
            <p className="font-heading text-3xl font-bold text-green-600">
              {contacts.filter(c => c.service === 'biancheria').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-text-muted text-sm">Tovagliato</p>
            <p className="font-heading text-3xl font-bold text-purple-600">
              {contacts.filter(c => c.service === 'tovagliato').length}
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex items-center gap-4">
          <Filter className="w-5 h-5 text-text-muted" />
          <Select value={filterService} onValueChange={setFilterService}>
            <SelectTrigger data-testid="admin-filter-select" className="w-48">
              <SelectValue placeholder="Filtra per servizio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i servizi</SelectItem>
              <SelectItem value="pulizie">Pulizie</SelectItem>
              <SelectItem value="biancheria">Biancheria</SelectItem>
              <SelectItem value="tovagliato">Tovagliato</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-text-muted text-sm">
            {filteredContacts.length} contatti
          </span>
        </div>

        {/* Contacts List */}
        <div className="space-y-4">
          {filteredContacts.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <p className="text-text-muted">Nessun contatto trovato</p>
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="font-heading text-xl font-bold text-text-primary">
                        {getContactName(contact)}
                      </h3>
                      <Badge className={getServiceBadge(contact.service)}>
                        {contact.service || 'generale'}
                      </Badge>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Building className="w-4 h-4" />
                        <span>{contact.contactPerson || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${contact.email}`} className="hover:text-primary">
                          {contact.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${contact.phone}`} className="hover:text-primary">
                          {contact.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(contact.created_at)}</span>
                      </div>
                    </div>

                    {contact.message && (
                      <p className="mt-3 text-text-secondary bg-gray-50 rounded-lg p-3 text-sm">
                        {contact.message}
                      </p>
                    )}

                    {/* Service-specific details */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {contact.serviceType && (
                        <Badge variant="outline">Tipo: {contact.serviceType}</Badge>
                      )}
                      {contact.squareMeters && (
                        <Badge variant="outline">{contact.squareMeters} mq</Badge>
                      )}
                      {contact.bedCount && (
                        <Badge variant="outline">{contact.bedCount} letti</Badge>
                      )}
                      {contact.frequency && (
                        <Badge variant="outline">{contact.frequency}</Badge>
                      )}
                      {contact.tableCount && (
                        <Badge variant="outline">{contact.tableCount} tavoli</Badge>
                      )}
                      {contact.stylePreference && (
                        <Badge variant="outline">{contact.stylePreference}</Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex md:flex-col gap-2">
                    <Button
                      size="sm"
                      className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
                      onClick={() => window.open(`https://wa.me/39${contact.phone?.replace(/\D/g, '')}`, '_blank')}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => deleteContact(contact.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
